class PrimerryProductMain extends HTMLElement {
  /**
   * One PDP accordion panel open at a time, for browsers without the details name attribute.
   *
   * The markup already carries name="PrimerryPdpAccordion-<section>", which is how the HTML
   * spec groups panels, and where that is supported the browser closes the others itself and
   * this listener finds nothing left to close. Safari before 17.2 and Firefox before 130
   * ignore the attribute, and there the panels all stayed open, which is what Kanak reported
   * on 16 Sep 2026.
   */
  groupAccordions() {
    if ('name' in HTMLDetailsElement.prototype) return;
    const group = this.querySelector('[data-pdp-accordions]');
    if (!group) return;
    const panels = Array.from(group.querySelectorAll('details'));
    panels.forEach((panel) => {
      panel.addEventListener('toggle', () => {
        if (!panel.open) return;
        panels.forEach((other) => {
          if (other !== panel) other.open = false;
        });
      });
    });
  }

  connectedCallback() {
    this.groupAccordions();
    this.kids = 1;
    this.packCount = Number(this.querySelector('[data-pdp-variant-card].is-active')?.dataset.packCount || 1);
    this.mediaStage = this.querySelector('[data-pdp-media-stage]');
    this.mediaScrollFrame = 0;
    this.dragState = null;
    this.addEventListener('click', this.handleClick.bind(this));
    this.addEventListener('change', this.handleChange.bind(this));
    this.addEventListener('submit', this.handleSubmit.bind(this));
    this.querySelector('[data-pdp-video-dialog]')?.addEventListener('click', (event) => {
      if (event.target === event.currentTarget) this.closeVideo();
    });
    this.querySelectorAll('[data-pdp-quick-dialog]').forEach((dialog) => {
      dialog.addEventListener('click', (event) => {
        if (event.target === dialog) this.closeQuickView(dialog);
      });
    });
    if (this.mediaStage) {
      this.mediaStage.addEventListener('scroll', () => this.scheduleMediaSync(), { passive: true });
      this.mediaStage.addEventListener('pointerdown', (event) => this.startMediaDrag(event));
      this.mediaStage.addEventListener('pointermove', (event) => this.moveMediaDrag(event));
      this.mediaStage.addEventListener('pointerup', (event) => this.endMediaDrag(event));
      this.mediaStage.addEventListener('pointercancel', (event) => this.endMediaDrag(event));
      this.mediaStage.addEventListener('dragstart', (event) => event.preventDefault());
    }
    this.initStickyAtc();
  }

  handleClick(event) {
    const thumb = event.target.closest('[data-pdp-thumb]');
    if (thumb && this.contains(thumb)) this.selectMedia(thumb.dataset.pdpThumb);

    const kid = event.target.closest('[data-pdp-kids]');
    if (kid && this.contains(kid)) this.selectKids(kid);

    if (event.target.closest('[data-pdp-video-open]')) this.openVideo();
    if (event.target.closest('[data-pdp-video-close]')) this.closeVideo();

    const popupPlay = event.target.closest('[data-pdp-popup-play]');
    if (popupPlay && this.contains(popupPlay)) {
      const video = this.querySelector('[data-pdp-video-dialog] video');
      if (video) {
        video.play().then(() => { popupPlay.hidden = true; }).catch(() => {});
        video.addEventListener('pause', () => { popupPlay.hidden = false; }, { once: true });
      }
      return;
    }

    const quickOpen = event.target.closest('[data-pdp-quick-open]');
    if (quickOpen && this.contains(quickOpen)) {
      if (quickOpen.hasAttribute('data-pdp-close-video-first')) this.closeVideo();
      this.openQuickView(quickOpen.dataset.pdpQuickOpen);
      return;
    }

    const quickClose = event.target.closest('[data-pdp-quick-close]');
    if (quickClose && this.contains(quickClose)) {
      this.closeQuickView(quickClose.closest('[data-pdp-quick-dialog]'));
      return;
    }

    const quickThumb = event.target.closest('[data-pdp-quick-thumb]');
    if (quickThumb && this.contains(quickThumb)) {
      const dialog = quickThumb.closest('[data-pdp-quick-dialog]');
      const image = dialog?.querySelector('.primerry-pdp-qv__main-image-element');
      if (image && quickThumb.dataset.imageSrc) {
        image.src = quickThumb.dataset.imageSrc;
        image.srcset = '';
        dialog.querySelectorAll('[data-pdp-quick-thumb]').forEach((item) => item.classList.toggle('is-active', item === quickThumb));
      }
      return;
    }

    const previous = event.target.closest('[data-pdp-upsell-prev]');
    const next = event.target.closest('[data-pdp-upsell-next]');
    if (previous || next) {
      const track = this.querySelector('[data-pdp-upsell-track]');
      const card = track?.querySelector('.primerry-pdp__upsell-card');
      if (track && card) track.scrollBy({ left: (card.offsetWidth + 12) * (next ? 1 : -1), behavior: 'smooth' });
    }
  }

  handleChange(event) {
    const stickySelect = event.target.closest('[data-pdp-sticky-variant]');
    if (stickySelect && this.contains(stickySelect)) {
      const card = this.querySelector(`[data-pdp-variant-card][data-variant-id="${CSS.escape(stickySelect.value)}"]`);
      const input = card?.querySelector('input[type="radio"]');
      if (input) input.checked = true;
      if (card) this.selectVariant(card);
      return;
    }

    const input = event.target.closest('input[name^="primerry-variant-"]');
    if (!input || !this.contains(input)) return;
    const card = input.closest('[data-pdp-variant-card]');
    if (card) this.selectVariant(card);
  }

  selectMedia(index) {
    const slide = this.querySelector(`[data-pdp-media="${CSS.escape(String(index))}"]`);
    if (this.mediaStage && slide) this.mediaStage.scrollTo({ left: Number(index) * this.mediaStage.clientWidth, behavior: 'smooth' });
    this.syncMediaState(Number(index));
  }

  syncMediaState(index) {
    this.querySelectorAll('[data-pdp-media]').forEach((media) => {
      if (Number(media.dataset.pdpMedia) !== index) media.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.querySelectorAll('[data-pdp-thumb]').forEach((thumb) => {
      const active = thumb.dataset.pdpThumb === String(index);
      thumb.classList.toggle('is-active', active);
      thumb.setAttribute('aria-current', active ? 'true' : 'false');
    });
  }

  scheduleMediaSync() {
    if (this.mediaScrollFrame) return;
    this.mediaScrollFrame = requestAnimationFrame(() => {
      this.mediaScrollFrame = 0;
      if (!this.mediaStage?.clientWidth) return;
      const index = Math.round(this.mediaStage.scrollLeft / this.mediaStage.clientWidth);
      this.syncMediaState(index);
    });
  }

  startMediaDrag(event) {
    if (event.pointerType !== 'mouse' || event.button !== 0 || event.target.closest('video, iframe, model-viewer, button, a')) return;
    this.dragState = { pointerId: event.pointerId, startX: event.clientX, scrollLeft: this.mediaStage.scrollLeft, moved: false };
    this.mediaStage.setPointerCapture(event.pointerId);
    this.mediaStage.classList.add('is-dragging');
  }

  moveMediaDrag(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
    const delta = event.clientX - this.dragState.startX;
    if (Math.abs(delta) > 3) this.dragState.moved = true;
    this.mediaStage.scrollLeft = this.dragState.scrollLeft - delta;
    if (this.dragState.moved) event.preventDefault();
  }

  endMediaDrag(event) {
    if (!this.dragState || this.dragState.pointerId !== event.pointerId) return;
    const width = this.mediaStage.clientWidth || 1;
    const velocityOffset = event.clientX - this.dragState.startX;
    let index = Math.round(this.mediaStage.scrollLeft / width);
    if (Math.abs(velocityOffset) > width * 0.12) index += velocityOffset < 0 ? 1 : -1;
    index = Math.max(0, Math.min(index, this.querySelectorAll('[data-pdp-media]').length - 1));
    this.mediaStage.classList.remove('is-dragging');
    this.dragState = null;
    this.mediaStage.scrollTo({ left: index * width, behavior: 'smooth' });
    this.syncMediaState(index);
  }

  selectKids(button) {
    this.kids = Number(button.dataset.pdpKids || 1);
    this.querySelectorAll('[data-pdp-kids]').forEach((item) => {
      const active = item === button;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    const quantity = this.querySelector('[data-pdp-quantity-input]');
    if (quantity) quantity.value = String(this.kids);
    const stickyQuantity = this.querySelector('[data-pdp-sticky-quantity]');
    if (stickyQuantity) stickyQuantity.value = String(this.kids);
    this.updateBottleMessage();
  }

  selectVariant(card) {
    this.querySelectorAll('[data-pdp-variant-card]').forEach((item) => item.classList.toggle('is-active', item === card));
    const variantInput = this.querySelector('[data-pdp-variant-input]');
    if (variantInput) variantInput.value = card.dataset.variantId;
    this.packCount = Number(card.dataset.packCount || 1);
    this.updateBottleMessage();

    const price = this.querySelector('[data-pdp-price]');
    const compare = this.querySelector('[data-pdp-compare]');
    const discount = this.querySelector('[data-pdp-discount]');
    if (price) price.textContent = card.dataset.price || '';
    if (compare) {
      compare.textContent = card.dataset.compare || '';
      compare.hidden = !card.dataset.compare;
    }
    if (discount) {
      discount.textContent = card.dataset.discount || '';
      discount.hidden = !card.dataset.compare;
    }

    const button = this.querySelector('[data-pdp-form] [type="submit"]');
    if (button) {
      const available = card.dataset.available === 'true';
      button.disabled = !available;
      button.textContent = available ? button.dataset.defaultLabel : 'sold out';
    }
    this.syncStickyVariant(card);
    const url = new URL(window.location.href);
    url.searchParams.set('variant', card.dataset.variantId);
    window.history.replaceState({}, '', url);
  }

  updateBottleMessage() {
    const count = this.kids * this.packCount;
    const message = this.querySelector('[data-pdp-bottles]');
    if (!message) return;
    const prefix = message.textContent.trim().split(/\s+\d+\s+/)[0] || "you'll get";
    message.textContent = `${prefix} ${count} ${count === 1 ? 'bottle' : 'bottles'}`;
  }

  initStickyAtc() {
    this.stickyAtc = this.querySelector('[data-pdp-sticky-atc]');
    const mainButton = this.querySelector('[data-pdp-form] .primerry-pdp__add');
    if (!this.stickyAtc || !mainButton) return;
    this.stickyObserver = new IntersectionObserver(([entry]) => {
      const shouldShow = !entry.isIntersecting && entry.boundingClientRect.bottom < 0;
      this.stickyAtc.classList.toggle('is-visible', shouldShow);
      this.stickyAtc.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
    }, { threshold: 0 });
    this.stickyObserver.observe(mainButton);
  }

  syncStickyVariant(card) {
    const select = this.querySelector('[data-pdp-sticky-variant]');
    if (!select) return;
    select.value = card.dataset.variantId;
    const option = select.selectedOptions[0];
    const price = this.querySelector('[data-pdp-sticky-price]');
    const compare = this.querySelector('[data-pdp-sticky-compare]');
    const discount = this.querySelector('[data-pdp-sticky-discount]');
    const button = this.querySelector('[data-pdp-sticky-form] [type="submit"]');
    const available = card.dataset.available === 'true';
    if (price) price.textContent = card.dataset.price || option?.dataset.price || '';
    if (compare) {
      compare.textContent = card.dataset.compare || option?.dataset.compare || '';
      compare.hidden = !compare.textContent;
    }
    if (discount) {
      discount.textContent = card.dataset.discount || option?.dataset.discount || '';
      discount.hidden = !compare?.textContent;
    }
    if (button) {
      button.disabled = !available;
      const buttonLabel = button.querySelector('[data-pdp-sticky-button-label]');
      const label = available ? button.dataset.defaultLabel : 'sold out';
      if (buttonLabel) buttonLabel.textContent = label;
      else button.textContent = label;
      button.setAttribute('aria-label', label);
    }
  }

  openVideo() {
    const dialog = this.querySelector('[data-pdp-video-dialog]');
    if (!dialog) return;
    dialog.showModal();
    document.documentElement.style.overflow = 'hidden';
    document.documentElement.classList.add('primerry-pdp-modal-open');
    const playButton = dialog.querySelector('[data-pdp-popup-play]');
    if (playButton) playButton.hidden = false;
  }

  closeVideo() {
    const dialog = this.querySelector('[data-pdp-video-dialog]');
    dialog?.querySelectorAll('video').forEach((video) => video.pause());
    const playButton = dialog?.querySelector('[data-pdp-popup-play]');
    if (playButton) playButton.hidden = false;
    dialog?.close();
    document.documentElement.style.overflow = '';
    document.documentElement.classList.remove('primerry-pdp-modal-open');
  }

  openQuickView(id) {
    const dialog = id ? this.querySelector(`#${CSS.escape(id)}`) : null;
    if (!dialog || dialog.open) return;
    dialog.showModal();
    document.documentElement.classList.add('primerry-pdp-modal-open');
  }

  closeQuickView(dialog) {
    if (!dialog) return;
    dialog.close();
    document.documentElement.classList.remove('primerry-pdp-modal-open');
  }

  async handleSubmit(event) {
    const form = event.target.closest('[data-pdp-form], [data-pdp-qv-form], [data-pdp-sticky-form]');
    if (!form || !this.contains(form)) return;
    event.preventDefault();
    const button = form.querySelector('[type="submit"]');
    const error = form.querySelector('[data-pdp-error]');
    if (!button || button.dataset.loading === 'true') return;
    const wasDisabled = button.disabled;
    button.dataset.loading = 'true';
    button.disabled = true;
    const buttonLabel = button.querySelector('[data-pdp-sticky-button-label]');
    const defaultLabel = button.dataset.defaultLabel || buttonLabel?.textContent || button.textContent;
    if (buttonLabel) buttonLabel.textContent = 'adding…';
    else button.textContent = 'adding…';
    button.setAttribute('aria-label', 'adding…');
    if (error) error.hidden = true;

    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
        credentials: 'same-origin',
      });
      const item = await response.json();
      if (!response.ok) throw new Error(item.description || item.message || 'Unable to add this product.');

      const [cart, sections] = await Promise.all([
        fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((result) => result.json()),
        fetch(`${window.location.pathname}?sections=cart-drawer-section`, { credentials: 'same-origin' }).then((result) => result.json()),
      ]);
      const currentDrawer = document.querySelector('#shopify-section-cart-drawer-section');
      if (sections['cart-drawer-section'] && currentDrawer) {
        const template = document.createElement('template');
        template.innerHTML = sections['cart-drawer-section'].trim();
        if (template.content.firstElementChild) currentDrawer.replaceWith(template.content.firstElementChild);
      }
      document.querySelectorAll('.primerry-header__cart-count').forEach((count) => {
        count.textContent = String(Math.min(cart.item_count, 99));
        count.classList.toggle('primerry-header__cart-count--empty', cart.item_count === 0);
      });
      const quickDialog = form.closest('[data-pdp-quick-dialog]');
      if (quickDialog) this.closeQuickView(quickDialog);
      requestAnimationFrame(() => document.querySelector('.primerry-header__cart')?.click());
    } catch (submitError) {
      if (error) {
        error.textContent = submitError instanceof Error ? submitError.message : 'Unable to add this product.';
        error.hidden = false;
      }
    } finally {
      button.dataset.loading = 'false';
      button.disabled = wasDisabled;
      if (buttonLabel) buttonLabel.textContent = defaultLabel;
      else button.textContent = defaultLabel;
      button.setAttribute('aria-label', defaultLabel);
    }
  }
}

if (!customElements.get('primerry-product-main')) customElements.define('primerry-product-main', PrimerryProductMain);
